/**
 * Partners whose tracking URL must never appear in served HTML, JSON or email.
 *
 * A raw tracking href is followed by crawlers, link checkers and email
 * security scanners straight to the partner, where every fetch books a click
 * we cannot filter. 90 days to 2026-10-07: PartnerStack counted 119,336 Deel
 * clicks against our 1,271 person clicks, 0 signups, and the Network declined
 * the account as "Association with potentially fraudulent accounts". Linking
 * through `/<locale>/go/<slug>` puts our bot gate (204 for bots, our own page
 * for hops no person clicked) between every fetch and the partner.
 *
 * Only single-link providers belong here: the go-route resolves the slug to
 * one target, so a multi-item grid (Shopee) cannot ride it.
 */
export const GO_LINK_PROVIDERS: ReadonlySet<string> = new Set([
	"deel",
	"remote",
	"wise",
]);

/** Whether this provider's links must go through the go-route. */
export function usesGoLink(provider: string): boolean {
	return GO_LINK_PROVIDERS.has(provider);
}

/**
 * Same-origin go-link for an on-site CTA. `surface` and `cid` let the
 * server-written click join the impression that minted the id (the Hunt
 * sponsored card pattern) — the caller must then skip its own client click
 * write, or the click counts twice.
 */
export function goLinkHref(
	slug: string,
	locale: string,
	opts: { surface?: string; cid?: string } = {},
): string {
	const params = new URLSearchParams();
	if (opts.surface) params.set("surface", opts.surface);
	if (opts.cid) params.set("cid", opts.cid);
	const query = params.toString();
	return `/${locale}/go/${encodeURIComponent(slug)}${query ? `?${query}` : ""}`;
}

/**
 * Absolute go-link for off-site surfaces (email, social). `src` labels the
 * click's surface, like the email footer sponsor's `?src=email_footer`.
 */
export function absoluteGoLink(
	baseUrl: string,
	slug: string,
	locale: string,
	src: string,
): string {
	const url = new URL(`/${locale}/go/${encodeURIComponent(slug)}`, baseUrl);
	url.searchParams.set("src", src);
	return url.toString();
}
